import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, Input } from '@mono/ui-components';
import { Search, X } from 'lucide-react';
import { ComplianceStatus as ComplianceStatusEnum, ObligationStrength as ObligationStrengthEnum } from '../../../types/proposal';
const obligations = [
    ObligationStrengthEnum.SHALL,
    ObligationStrengthEnum.MUST,
    ObligationStrengthEnum.WILL,
    ObligationStrengthEnum.SHOULD,
    ObligationStrengthEnum.MAY
];
const obligationColors = {
    [ObligationStrengthEnum.SHALL]: 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200',
    [ObligationStrengthEnum.SHALL_NOT]: 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200',
    [ObligationStrengthEnum.MUST]: 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200',
    [ObligationStrengthEnum.MUST_NOT]: 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200',
    [ObligationStrengthEnum.WILL]: 'bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200',
    [ObligationStrengthEnum.WILL_NOT]: 'bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200',
    [ObligationStrengthEnum.SHOULD]: 'bg-yellow-100 text-yellow-700 border-yellow-300 hover:bg-yellow-200',
    [ObligationStrengthEnum.SHOULD_NOT]: 'bg-yellow-100 text-yellow-700 border-yellow-300 hover:bg-yellow-200',
    [ObligationStrengthEnum.MAY]: 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200',
    [ObligationStrengthEnum.MAY_NOT]: 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
};
const statusOptions = [
    {
        value: ComplianceStatusEnum.NOT_STARTED,
        label: 'Not Started',
        activeColor: 'bg-gray-100 text-gray-700 border-gray-400'
    },
    {
        value: ComplianceStatusEnum.IN_PROGRESS,
        label: 'In Progress',
        activeColor: 'bg-blue-100 text-blue-700 border-blue-400'
    },
    {
        value: ComplianceStatusEnum.ADDRESSED,
        label: 'Addressed',
        activeColor: 'bg-green-100 text-green-700 border-green-400'
    },
    {
        value: ComplianceStatusEnum.PARTIAL,
        label: 'Partial',
        activeColor: 'bg-yellow-100 text-yellow-700 border-yellow-400'
    },
    {
        value: ComplianceStatusEnum.NON_COMPLIANT,
        label: 'Non-Compliant',
        activeColor: 'bg-red-100 text-red-700 border-red-400'
    },
    {
        value: ComplianceStatusEnum.NOT_APPLICABLE,
        label: 'N/A',
        activeColor: 'bg-gray-100 text-gray-500 border-gray-400'
    }
];
/**
 * Filter and search bar for requirements list
 * Supports searching by text, filtering by obligation strength, and filtering by compliance status
 */ export const RequirementFilters = ({ searchTerm, onSearchChange, selectedObligations, onObligationToggle, statusFilter, onStatusFilterChange })=>{
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "relative",
                children: [
                    /*#__PURE__*/ _jsx(Search, {
                        className: "absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                    }),
                    /*#__PURE__*/ _jsx(Input, {
                        type: "text",
                        placeholder: "Search requirements...",
                        value: searchTerm,
                        onChange: (e)=>onSearchChange(e.target.value),
                        className: "pl-10 pr-10"
                    }),
                    searchTerm && /*#__PURE__*/ _jsx("button", {
                        onClick: ()=>onSearchChange(''),
                        className: "absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded",
                        "aria-label": "Clear search",
                        children: /*#__PURE__*/ _jsx(X, {
                            className: "w-4 h-4 text-gray-400"
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("label", {
                        className: "text-xs font-medium text-gray-700 uppercase tracking-wide",
                        children: "Obligation Strength"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex flex-wrap gap-2",
                        children: obligations.map((obligation)=>{
                            const isSelected = selectedObligations.includes(obligation);
                            return /*#__PURE__*/ _jsx("button", {
                                onClick: ()=>onObligationToggle(obligation),
                                className: "transition-all",
                                children: /*#__PURE__*/ _jsx(Badge, {
                                    variant: "outline",
                                    className: `text-xs font-medium border cursor-pointer ${isSelected ? obligationColors[obligation] : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'}`,
                                    children: obligation
                                })
                            }, obligation);
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("label", {
                        className: "text-xs font-medium text-gray-700 uppercase tracking-wide",
                        children: "Compliance Status"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex flex-wrap gap-2",
                        children: statusOptions.map((option)=>{
                            const isSelected = statusFilter.includes(option.value);
                            return /*#__PURE__*/ _jsx("button", {
                                onClick: ()=>onStatusFilterChange(option.value),
                                className: "transition-all",
                                children: /*#__PURE__*/ _jsx(Badge, {
                                    variant: "outline",
                                    className: `text-xs font-medium border cursor-pointer ${isSelected ? option.activeColor : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'}`,
                                    children: option.label
                                })
                            }, option.value);
                        })
                    })
                ]
            })
        ]
    });
};
